import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';

import { isInvoiceBillingType } from '~constants/maps';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { ContainerHeader, ErrorResponse } from '~components';
import { faCopy } from '~components/icon';
import { Form as WizardForm } from '~components/wizard-form';

import { Budget, COPY_STATUS } from '~support-at-home/models';

import copyBudgetFields from '~support-at-home/forms/budgets/copyBudget';
import { findDateInvalidItems } from '~support-at-home/forms/budgets/servicePeriods';

import { getAll as getClients } from '~main/actions/clients';
import {
  useGetBudgetQuery,
  useLazyGetFilteredServicesQuery,
  useSaveBudgetMutation,
} from '~support-at-home/actions/budgets';
import { getAll as getClientBudgetStatuses } from '~support-at-home/actions/clientBudgetStatuses';
import { getAll as getClientContributions } from '~support-at-home/actions/clientContributions';
import { getAll as getClientServices } from '~support-at-home/actions/clientServices';
import { getAll as getPricePlans } from '~support-at-home/actions/pricePlans';
import { getAll as getSahBudgets } from '~support-at-home/actions/sahBudgets';

import { toggleQuickMenu } from '~main/reducers/app';

/** Long enough to read a line's name and the range it has to fit inside. */
const DATE_TOAST_MS = 8000;

/** `beforeStepChange` numbers steps from zero, so Sections & Items - the fourth step - is 3. */
const SECTIONS_AND_ITEMS_STEP = 3;

const BudgetCopy: React.FC = () => {
  const { id: sourceId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [saveBudget] = useSaveBudgetMutation();
  // Lazy rather than a standing query: the arguments only exist once the provider has chosen a price
  // plan and a package part way through the wizard, and by the time this runs the Sections & Items
  // step has already fetched the same entry under the same key, so the trigger resolves from cache.
  const [fetchFilteredServices] = useLazyGetFilteredServicesQuery();
  const [isReady, setIsReady] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);

  // Fetch the source budget to pre-fill values
  const {
    data: sourceBudget,
    error: sourceError,
    isLoading,
  } = useGetBudgetQuery(sourceId ? { id: sourceId } : skipToken);

  // Load lookup data when source budget is available
  useEffect(() => {
    dispatch(getClients());
    dispatch(getClientBudgetStatuses());
    dispatch(toggleQuickMenu(true));

    return () => {
      dispatch(toggleQuickMenu(false));
    };
  }, []);

  // Load client-specific data once we have the source budget
  useEffect(() => {
    if (!sourceBudget?.client_id) return;

    const loadClientData = async () => {
      try {
        await Promise.all([
          dispatch(getPricePlans()),
          dispatch(getSahBudgets({ client_id: sourceBudget.client_id })),
          dispatch(getClientContributions({ client_id: sourceBudget.client_id })),
          dispatch(getClientServices({ client_id: sourceBudget.client_id })),
        ]);
      } catch (err) {
        console.error('Failed to load copy budget lookups:', err);
        setLookupError('Some lookup data failed to load. Selectors may be incomplete.');
      } finally {
        setIsReady(true);
      }
    };

    loadClientData();
  }, [sourceBudget?.client_id]);

  const initialValues = useMemo(() => {
    if (!sourceBudget) return {} as Partial<Budget>;

    return {
      client_id: sourceBudget.client_id,
      client_sah_budget_id: null,
      price_plan_id: sourceBudget.price_plan_id,
      price_plan_revision_id: sourceBudget.price_plan_revision_id,
      package_fixed_id: sourceBudget.package_fixed_id,
      start_date: '',
      end_date: '',
      total_amount: sourceBudget.client_sah_budget_id ? 0 : sourceBudget.total_amount,
      total_includes_case_management: sourceBudget.client_sah_budget_id
        ? null
        : sourceBudget.total_includes_case_management,
      ignore_sah_filters: sourceBudget.ignore_sah_filters ?? false,
      supplements: sourceBudget.supplements ?? [],
      sections: [],
      total_allocated: 0,
      source_budget_id: sourceId,
    } as unknown as Partial<Budget> & { source_budget_id: string };
  }, [sourceBudget, sourceId]);

  const onSaveCopy = async (values: Budget) => {
    setSaveError(null);

    // Filter out removed items before saving
    const filteredSections = (values.sections ?? []).map((section) => ({
      ...section,
      items: section.items.filter((item) => item.copy_status !== COPY_STATUS.REMOVED),
    }));

    const newBudget = {
      client_id: values.client_id,
      client_sah_budget_id: values.client_sah_budget_id,
      price_plan_revision_id: values.price_plan_revision_id,
      client_package_id: values.package_fixed_id,
      total_includes_case_management: values.total_includes_case_management,
      ignore_sah_filters: values.ignore_sah_filters,
      supplements: values.supplements,
      start_date: values.start_date,
      status_id: values.status_id,
      end_date: values.end_date,
      total_amount: values.total_amount,
      sections: filteredSections,
    };

    try {
      const result = await saveBudget(newBudget).unwrap();
      if (result?.id) {
        navigate('/support-at-home/budgets');
        return true;
      }
      setSaveError('Budget was saved but no ID was returned. Please check the budgets list.');
    } catch (err) {
      console.error('Error saving copied budget:', err);
      setSaveError(getErrorMessage(err));
    }
    return false;
  };

  const onCancel = () => navigate('/support-at-home/budgets');

  const beforeStepChange = async (step: number, values: Budget, direction: 'next' | 'prev' = 'next') => {
    // Sections & Items is index 3 - beforeStepChange numbers steps from zero. Review's own forward
    // path is Submit, not Next, so a check gated on step 4 never runs going forward - the only call
    // that ever reached it was 'prev', which had no direction guard and so blocked Back too, trapping
    // a provider on the read-only Review step with no way out. Both rules now live here instead,
    // alongside the date-period check they sit next to in the save's own validation.
    //
    // The save asserts all three rules, so a line that breaks one produces an error at the top of the
    // Review step - which is read only, has no edit control, and says nothing about which line it
    // means or what to do about it. Copying a budget that has been through the personal care cutover
    // into dates after it lands exactly there. Holding here keeps the provider on the one step where
    // the line can still be re-dated or removed, and names it.
    //
    // Forward only: a guard that also fired on Back would trap a provider on the very step it is
    // telling them to go and fix.
    if (step === SECTIONS_AND_ITEMS_STEP && direction === 'next') {
      const editableItems = (values.sections ?? []).flatMap((section) =>
        section.items.filter((item) => item.copy_status !== COPY_STATUS.REMOVED),
      );

      const hasInvalidItem = editableItems.some((item) => {
        const { billing_type, start_date, end_date, shift_id, frequency_id } = item;
        if (!start_date || !end_date) return true;
        if (!billing_type || !isInvoiceBillingType(billing_type)) {
          return !shift_id || !frequency_id;
        }
        return false;
      });

      if (hasInvalidItem) {
        showToast(
          'danger',
          'All chosen service types must have valid start/end dates. Shift & frequency must also be selected if available.',
          5000,
        );
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return false;
      }

      // A lookup that fails must not become a wall. This guard exists to catch the provider earlier
      // and more clearly than the save does - it is not the authority on the rule, the server is - so
      // with no services to check against, let them through and leave the save to its own assertion.
      //
      // start_date/end_date are sent so the entries come back scoped to this budget's window, the
      // same reason BudgetSections sends them - without it a service that ends part way through looks
      // available for the whole budget, and the args stop matching that step's cache entry so this
      // becomes a fresh fetch on every Next instead of resolving from cache.
      let filteredServices: any[] = [];
      try {
        filteredServices = await fetchFilteredServices(
          {
            revision_id: values.price_plan_revision_id,
            client_id: Number(values.client_id),
            package_fixed_id: Number(values.package_fixed_id),
            ignore_sah_filters: values.ignore_sah_filters ?? false,
            start_date: values.start_date,
            end_date: values.end_date,
          },
          true,
        ).unwrap();
      } catch (err) {
        console.error('Failed to load filtered services for the date check:', err);
        return true;
      }

      const dateInvalid = findDateInvalidItems(
        values.sections ?? [],
        { start_date: values.start_date ?? null, end_date: values.end_date ?? null },
        filteredServices ?? [],
      );

      if (dateInvalid.length > 0) {
        showToast(
          'danger',
          `${dateInvalid[0].reason} Re-date the line to a period the service covers, or remove it, before continuing.`,
          DATE_TOAST_MS,
        );
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return false;
      }
    }
    return true;
  };

  if (sourceError) {
    return (
      <div className="p-2 budget">
        <ContainerHeader icon={faCopy} iconSize="1x" className="info" dataCy="copy-budget-header">
          Copy Budget
        </ContainerHeader>
        <ErrorResponse message={getErrorMessage(sourceError)} />
      </div>
    );
  }

  if (isLoading || !isReady) {
    return (
      <div className="p-2 budget">
        <ContainerHeader icon={faCopy} iconSize="1x" className="info" dataCy="copy-budget-header">
          Copy Budget
        </ContainerHeader>
        <div className="text-muted p-3">Loading source budget...</div>
      </div>
    );
  }

  return (
    <div className="p-2 budget">
      <ContainerHeader icon={faCopy} iconSize="1x" className="info" dataCy="copy-budget-header">
        Copy Budget
      </ContainerHeader>
      {lookupError && <ErrorResponse message={lookupError} />}
      {saveError && <ErrorResponse message={saveError} />}
      <WizardForm
        steps={copyBudgetFields}
        initialValues={initialValues as unknown as Budget}
        bodyClassname="d-flex flex-column flex-grow-1"
        onComplete={onSaveCopy}
        onCancel={onCancel}
        beforeStepChange={beforeStepChange}
      />
    </div>
  );
};

export default BudgetCopy;
